import { PopoverProps, Typography, useTheme } from '@mui/material';
import { Stack } from '@mui/system';
import { graphql } from '../gql';
import { useApiMutation } from '../query/hooks';
import { FC, useRef, useState } from 'react';
import { AutoGraphOutlined, DeleteOutline, EditOutlined, FileDownloadOutlined, MoreHoriz, PaletteOutlined } from '@mui/icons-material';
import { FormattedMessage, useIntl } from 'react-intl';
import { CustomPopover } from '../ui/CustomPopover';
import { useSnackbar } from '../store/snackbar';
import { ColorPicker } from '../components/ColorPicker';
import { useModalContext } from '../modal/ModalContext';
import { useNavigate } from 'react-router';
import { MainMetricInfo } from './detail/detailView/MainMetricInfo';
import { useGeneratePDF } from '../hooks/useGeneratePDF';

const updateJourneyColorMutation = graphql(`
	mutation journey_updateColor($id: UUID!, $color: String!) {
		updateJourney(by: { id: $id }, data: { color: $color }) {
			ok
			errorMessage
		}
	}
`);

type Props = Readonly<{
	journeyId: string;
	title: string | undefined;
	journeyColor?: string | null;
	mainMetricId: string | null;
	headerRedirect?: () => void;
	onEditDrawer: () => void;
	headerActionsComponent: JSX.Element | null;
	metricUnitSign: string | null;
}>;

export const JourneyHeader: FC<Props> = ({
	journeyId,
	title,
	journeyColor,
	mainMetricId,
	headerRedirect,
	onEditDrawer,
	headerActionsComponent,
	metricUnitSign,
}) => {
	const theme = useTheme();
	const intl = useIntl();
	const [colorPopoverOpen, setColorPopoverOpen] = useState<boolean>(false);
	const ref = useRef<HTMLLIElement>(null);
	const [colorPopoverAnchorEl, setColorPopoverAnchorEl] = useState<PopoverProps['anchorEl']>(null);
	const forceCloseCustomPopoverEvent = useRef<Event>(new Event('forceClosePopover-color-change'));
	const { setModal } = useModalContext();
	const { mutate: updateJourneyColor } = useApiMutation(updateJourneyColorMutation);
	const navigate = useNavigate();
	const { openSnackbar } = useSnackbar();
	const { generatePdf } = useGeneratePDF();

	return (
		<Stack gap={{ xs: 1, lg: 2 }} py={2} px={{ xs: 2, lg: 3 }}>
			<Stack direction={'row'} alignItems={'center'} gap={2}>
				<Typography
					onClick={headerRedirect}
					sx={{ cursor: headerRedirect != null ? 'pointer' : 'unset' }}
					variant={'h1'}
					fontSize={24}
					fontWeight={700}
				>
					{title}
				</Typography>
				<CustomPopover
					layoutIcon={<MoreHoriz />}
					forceCloseEvent={forceCloseCustomPopoverEvent.current}
					closeOnAction={false}
					options={[
						{
							icon: <EditOutlined sx={{ fontSize: 16, fill: theme.palette.grey[500] }} />,
							caption: <FormattedMessage id={'common.edit'} />,
							onAction: () => {
								onEditDrawer();
								window.dispatchEvent(forceCloseCustomPopoverEvent.current);
							},
						},
						{
							icon: <AutoGraphOutlined sx={{ fontSize: 16, fill: theme.palette.grey[500] }} />,
							caption: <FormattedMessage id={'journey.popover.metrics'} />,
							onAction: () => {
								navigate('/connections/metrics');
							},
						},
						{
							icon: <PaletteOutlined sx={{ fontSize: 16, fill: theme.palette.grey[500] }} />,
							caption: <FormattedMessage id={'journey.popover.set-color'} />,
							ref: ref,
							onAction: () => {
								const getBoundingClientRect = () => {
									return ref.current!.getBoundingClientRect();
								};

								setColorPopoverOpen((prevState) => !prevState);
								setColorPopoverAnchorEl({ getBoundingClientRect, nodeType: 1 });
							},
						},
						{
							icon: <FileDownloadOutlined sx={{ fontSize: 16, fill: theme.palette.grey[500] }} />,
							caption: <FormattedMessage id={'journey.popover.export'} />,
							onAction: async () => await generatePdf(),
						},
						{
							icon: <DeleteOutline sx={{ fontSize: 16, fill: theme.palette.error.main }} />,
							caption: (
								<Typography color={theme.palette.error.main}>
									<FormattedMessage id={'journey.popover.delete'} />
								</Typography>
							),
							onAction: () => {
								window.dispatchEvent(forceCloseCustomPopoverEvent.current);
								setModal({ type: 'deleteJourney', payload: { id: journeyId, name: title ?? '' } });
							},
						},
					]}
				/>
				<ColorPicker
					open={colorPopoverOpen}
					defaultValue={journeyColor}
					anchorEl={colorPopoverAnchorEl}
					onClose={() => setColorPopoverOpen(false)}
					onChange={(event) => {
						updateJourneyColor(
							{ id: journeyId, color: event.target.value },
							{
								onSuccess: (result) => {
									if (!result.updateJourney.ok) {
										console.error(result.updateJourney.errorMessage);
										openSnackbar({
											open: true,
											message: intl.formatMessage({ id: 'common.error.default' }),
											variant: 'alert',
											alert: {
												color: 'error',
											},
											close: true,
										});
									}
									setColorPopoverOpen(false);
									window.dispatchEvent(forceCloseCustomPopoverEvent.current);
								},
							},
						);
					}}
				/>
				{mainMetricId && <MainMetricInfo metricId={mainMetricId} unitSign={metricUnitSign} />}
			</Stack>
			{headerActionsComponent}
		</Stack>
	);
};
